// Project overview (/p/:key/overview, ontwerp §11.4, §12.3): one card per
// stream with its numbers, what is waiting on you, and what runs right now.

import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import type { Project, ProjectOverviewStream } from '@goblic/shared'
import { boardApi } from '../../lib/api-client'
import { useProjectEvents } from '../../hooks/useProjectEvents'
import { RequireProject } from '../../lib/project-context'
import { EmptyState, ErrorNote, LoadingRows, PageHeader, Panel } from '../../components/ui'
import { MomentArt, sceneClass } from '../../components/goblin-art'
import {
  PULL_BLOCKED_TEXT,
  STREAM_COLOR_CLASS,
} from '../../components/board/board-utils'
import { formatMoney, formatMs, formatRelative, humanize } from '../../lib/format'

export function ProjectOverviewPage() {
  return <RequireProject>{(project) => <OverviewContent project={project} />}</RequireProject>
}

function pullState(entry: ProjectOverviewStream): { label: string; tone: string } {
  const { stream, pullBlockedReason } = entry
  if (stream.paused) return { label: 'Paused', tone: 'text-signal' }
  if (!stream.autoPull) return { label: 'Auto-pull off', tone: 'text-ink-3' }
  if (pullBlockedReason === 'budget_exhausted') return { label: 'Budget used up', tone: 'text-signal' }
  if (pullBlockedReason === 'wip_full') return { label: 'Auto-pull on, WIP full', tone: 'text-ink-2' }
  return { label: 'Auto-pull on', tone: 'text-moss' }
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-sm text-ink-3">{label}</dt>
      <dd className="text-base font-semibold tabular-nums text-ink">{value}</dd>
    </div>
  )
}

function StreamCard({ project, entry }: { project: Project; entry: ProjectOverviewStream }) {
  const { stream, stats } = entry
  const color = STREAM_COLOR_CLASS[stream.color]
  const pull = pullState(entry)
  const spent = formatMoney(stats.spentThisWeekCents)
  return (
    <Link
      to="/p/$projectKey/boards/$streamKey"
      params={{ projectKey: project.key, streamKey: stream.key }}
      className="panel block p-4 transition-colors hover:bg-sunken/50"
    >
      <div className="flex items-center justify-between gap-3">
        <span className="flex min-w-0 items-center gap-2">
          <span aria-hidden className={`h-2.5 w-2.5 shrink-0 rounded-full ${color.dot}`} />
          <span className="truncate font-display text-md font-semibold text-ink">{stream.name}</span>
        </span>
        <span className={`shrink-0 text-sm ${pull.tone}`}>{pull.label}</span>
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
        <Stat
          label="In flight"
          value={stats.wipLimit != null ? `${stats.inFlight} / ${stats.wipLimit}` : String(stats.inFlight)}
        />
        <Stat label="Ready" value={String(stream.lanes.backlogReady ? stats.ready : stats.inbox)} />
        <Stat
          label="Cycle time"
          value={stats.medianCycleTimeMs != null ? formatMs(stats.medianCycleTimeMs) : '—'}
        />
        <Stat
          label="This week"
          value={
            stats.weeklyBudgetCents != null
              ? `${spent} / ${formatMoney(stats.weeklyBudgetCents)}`
              : spent
          }
        />
      </dl>
      {stats.blocked > 0 ? <p className="mt-3 text-sm text-danger">{stats.blocked} blocked</p> : null}
      {entry.pullBlockedReason && entry.pullBlockedReason !== 'auto_pull_off' && !stream.paused ? (
        <p className="mt-1 text-sm text-ink-3">{PULL_BLOCKED_TEXT[entry.pullBlockedReason]}</p>
      ) : null}
    </Link>
  )
}

function OverviewContent({ project }: { project: Project }) {
  useProjectEvents(project.id)
  const overviewQuery = useQuery({
    queryKey: ['project', project.id, 'overview'],
    queryFn: () => boardApi.overview(project.id),
    refetchInterval: 15_000,
  })
  const overview = overviewQuery.data

  return (
    <div className="space-y-6">
      <PageHeader
        title={
          <>
            {project.name}{' '}
            <span className="font-mono text-base font-normal text-ink-3">{project.key}</span>
          </>
        }
        description={project.description ?? 'Streams, what needs you, and what is running.'}
        actions={
          <Link
            to="/p/$projectKey/tasks"
            params={{ projectKey: project.key }}
            search={{ new: true }}
            className="btn-primary"
          >
            New task
          </Link>
        }
      />

      {overviewQuery.isLoading ? (
        <div className="panel">
          <LoadingRows />
        </div>
      ) : overviewQuery.isError || !overview ? (
        <div className="panel">
          <ErrorNote error={overviewQuery.error ?? new Error('No data.')} what="the overview" />
        </div>
      ) : (
        <>
          {overview.streams.length === 0 ? (
            <Panel flush>
              <EmptyState title="No streams yet" art="sorting">
                Every workflow becomes a stream with its own board. Add a workflow in{' '}
                <Link
                  to="/p/$projectKey/settings"
                  params={{ projectKey: project.key }}
                  search={{ tab: 'workflows' }}
                  className="link"
                >
                  the project settings
                </Link>
                .
              </EmptyState>
            </Panel>
          ) : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {overview.streams.map((entry) => (
                <StreamCard key={entry.stream.key} project={project} entry={entry} />
              ))}
            </div>
          )}

          <Panel title="Waiting on you" flush>
            {overview.waitingOnYou.length === 0 ? (
              <div className="flex items-center gap-3 px-5 py-4">
                <MomentArt moment="empty" size={56} className="-my-2" />
                <p className="text-base text-ink-3">Nothing is waiting on you.</p>
              </div>
            ) : (
              <ul className="divide-y divide-line">
                {overview.waitingOnYou.map((item) => (
                  <li key={item.approvalId}>
                    <Link
                      to="/approvals"
                      search={{ id: item.approvalId }}
                      className="flex items-center justify-between gap-4 px-5 py-3 transition-colors hover:bg-sunken/60"
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-base font-medium text-ink">
                          {item.ticketKey ? (
                            <span className="mr-2 font-mono text-sm font-normal text-ink-3">
                              {item.ticketKey}
                            </span>
                          ) : null}
                          {item.title}
                        </span>
                        <span className="block truncate text-sm text-ink-3">
                          {humanize(item.type)}
                          {item.state ? `, ${humanize(item.state)}` : ''} · {formatRelative(item.since)}
                        </span>
                      </span>
                      <span className="btn-secondary btn-sm shrink-0">Review</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          <Panel title="Running now" flush className={sceneClass('workshop')}>
            {overview.runningNow.length === 0 ? (
              <div className="flex items-center gap-3 px-5 py-4">
                <MomentArt moment="sorting" size={56} className="-my-2" />
                <p className="text-base text-ink-3">No agents are working right now.</p>
              </div>
            ) : (
              <ul className="divide-y divide-line">
                {overview.runningNow.map((run) => (
                  <li key={run.runId}>
                    <Link
                      to="/runs/$runId"
                      params={{ runId: run.runId }}
                      className="flex items-center justify-between gap-4 px-5 py-3 transition-colors hover:bg-sunken/60"
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-base font-medium text-ink">
                          {run.ticketKey ? (
                            <span className="mr-2 font-mono text-sm font-normal text-ink-3">
                              {run.ticketKey}
                            </span>
                          ) : null}
                          {run.title}
                        </span>
                        <span className="block truncate text-sm text-ink-3">
                          {run.kind === 'decision' ? 'Decision' : (run.roleName ?? 'Agent')}
                          {run.agentName ? ` · ${run.agentName}` : ''}
                          {run.startedAt ? ` · started ${formatRelative(run.startedAt)}` : ''}
                        </span>
                      </span>
                      <span className="shrink-0 text-sm tabular-nums text-ink-3">
                        {formatMoney(run.costCents)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </>
      )}
    </div>
  )
}
